(function () {
  'use strict';

  // The rail owns labels, order, availability, selections, and navigation.
  // This surface only presents its next useful choice.
  var ROOT = 'collection-search-filters, [data-sfs-query-filters]';
  var LINK = '[data-sfs-filter-link], [data-sfs-query-filter-link]';
  var currentRoot;
  var observer;
  var queued = false;
  var signature = '';
  var renderedGroup;
  var expandedGroup;
  var mobileOpen = false;
  var fitOptions = function () {};
  var guideWidth = 0;
  var sizeObserver;
  var restoreGuideFocus = false;
  var skipped = new Set();
  var hadFilters = false;
  var searchGroups = [];

  function selected(link) {
    var row = link.closest('li');
    var input = row && row.querySelector('input');
    return link.getAttribute('aria-current') === 'true' || !!(input && input.checked);
  }

  function available(link) {
    var row = link.closest('li');
    // Local option searches must not change which facet the guide offers.
    return row && !row.hasAttribute('data-sfs-unavailable') &&
      link.getAttribute('aria-disabled') !== 'true';
  }

  function skipKey(kind, label) {
    return kind || label;
  }

  function anySelected(root) {
    return Array.prototype.some.call(root.querySelectorAll(LINK), selected);
  }

  // Skip is per page view. It resets when the last filter is cleared, not on every empty render.
  function resetSkippedIfCleared(root) {
    var any = anySelected(root);
    if (hadFilters && !any) skipped.clear();
    hadFilters = any;
  }

  function nextGroup(root) {
    var dynamicOrder = root.matches('[data-sfs-query-filters]');
    var order = { brand: 0, poles: 1, mounting: 2, amps: 3, series: 4, voltage: 5 };
    var groups = Array.prototype.slice.call(root.querySelectorAll('.sidebar-section .filter-block'));
    function kind(group) {
      var label = group.querySelector('summary .label');
      var text = label ? label.textContent.trim().toLowerCase() : '';
      if (/^(item|item type|product type|category|product category|condition|item condition)$/.test(text)) return 'excluded';
      if (/interrupting|\baic\b/.test(text)) return 'excluded';
      if (/brand|manufacturer/.test(text)) return 'brand';
      if (/pole/.test(text)) return 'poles';
      if (/mount/.test(text)) return 'mounting';
      if (/amp/.test(text)) return 'amps';
      if (/series/.test(text)) return 'series';
      if (/voltage/.test(text)) return 'voltage';
      return '';
    }
    resetSkippedIfCleared(root);
    if (!dynamicOrder) groups.sort(function (a, b) { return (order[kind(a)] ?? 6) - (order[kind(b)] ?? 6); });
    for (var i = 0; i < groups.length; i += 1) {
      var group = groups[i];
      if (group.hidden || group.closest('[hidden]') || group.hasAttribute('data-sfs-guide-redundant') ||
          kind(group) === 'excluded') continue;
      if (kind(group) === 'poles' && hasSpecialty(root)) continue;
      var links = Array.prototype.slice.call(group.querySelectorAll(LINK));
      if (links.some(selected)) continue;
      links = links.filter(available);
      if (links.length < 2) continue;
      var label = group.querySelector('summary .label');
      if (!label) continue;
      var labelText = label.textContent.trim();
      if (skipped.has(skipKey(kind(group), labelText))) continue;
      return { group: group, label: labelText, links: links, kind: kind(group) };
    }
    return null;
  }

  function followingGroup(root, current) {
    var key = skipKey(current.kind, current.label);
    skipped.add(key);
    var following = nextGroup(root);
    skipped.delete(key);
    return following;
  }

  function specialtyNames() {
    var template = document.querySelector('[data-facet-guide-specialties]');
    if (!template || !template.content) return [];
    return Array.prototype.slice.call(template.content.querySelectorAll('[data-breaker-specialty-link]')).map(function (link) {
      return (link.dataset.breakerLabel || '').trim();
    }).filter(Boolean);
  }

  function specialtyKey(value) {
    return String(value || '').trim().toLowerCase();
  }

  function isSpecialtyName(label, names) {
    var key = specialtyKey(label);
    return !!key && names.some(function (name) { return specialtyKey(name) === key; });
  }

  // Names come from the specialty catalog. Availability comes only from the
  // current rail: an unselected, enabled, in-scope filter link with a real href.
  function availableSpecialtyLinks(root, polesGroup) {
    var names = specialtyNames();
    if (!names.length) return [];
    var seen = new Set();
    var matches = [];
    Array.prototype.forEach.call(root.querySelectorAll(LINK), function (link) {
      if (polesGroup && polesGroup.contains(link)) return;
      if (!available(link) || selected(link)) return;
      var href = link.getAttribute('href');
      if (!href) return;
      var label = labelOf(link);
      var key = specialtyKey(label);
      if (!key || seen.has(key) || !isSpecialtyName(label, names)) return;
      seen.add(key);
      matches.push(link);
    });
    return matches;
  }

  function hasSpecialty(root) {
    var names = specialtyNames();
    if (!names.length) return false;
    var query = (new URL(window.location.href).searchParams.get('q') || '').toLowerCase();
    if (names.some(function (name) {
      return name && query.indexOf(name.toLowerCase()) >= 0;
    })) return true;
    if (!root) return false;
    return Array.prototype.some.call(root.querySelectorAll(LINK), function (link) {
      return selected(link) && isSpecialtyName(labelOf(link), names);
    });
  }

  function labelOf(link) {
    var row = link && link.closest('li');
    var label = row && row.querySelector('label .text');
    return (link && (link.getAttribute('data-sfs-label') || link.getAttribute('data-sfs-query-option-label'))) ||
      (label && label.textContent.trim()) || '';
  }

  // The left rail already tracks the truthful count of values still hidden
  // behind its own Show-more control (see syncEmbeddedOptions in
  // collection-search-filters.js): selectable tail values only, updated on
  // every availability payload. Reading that button's own text, rather than
  // re-deriving a count from the raw data-sfs-more-options JSON, keeps the
  // top rail's number identical to the left rail's at every point in time.
  function tailButtonFor(root, group) {
    var slug = group && group.getAttribute('data-sfs-group');
    if (!slug) return null;
    var button = root.querySelector('[data-sfs-show-more="' + slug + '"]');
    return button && !button.hidden ? button : null;
  }

  function tailRemaining(root, group) {
    var button = tailButtonFor(root, group);
    if (!button) return 0;
    var match = /(\d+)/.exec(button.textContent || '');
    return match ? parseInt(match[1], 10) : 0;
  }

  function busy(root) {
    return root.getAttribute('aria-busy') === 'true' || root.hasAttribute('data-sfs-loading');
  }

  function withoutFacet(href, group) {
    var url = new URL(href, window.location.href);
    var raw = url.searchParams.get('facets');
    if (!raw) return '';
    var remaining;
    try {
      var facets = JSON.parse(raw);
      if (!facets || Array.isArray(facets) || typeof facets !== 'object' ||
          !Object.prototype.hasOwnProperty.call(facets, group)) return '';
      delete facets[group];
      remaining = Object.keys(facets).length ? JSON.stringify(facets) : '';
    } catch (e) {
      var pairs = raw.split('|');
      var keep = pairs.filter(function (pair) { return pair.split(':')[0] !== group; });
      if (keep.length === pairs.length) return '';
      remaining = keep.join('|');
    }
    if (remaining) url.searchParams.set('facets', remaining);
    else url.searchParams.delete('facets');
    if (group === 'brand_canonical') url.searchParams.delete('sb_brand_label');
    url.searchParams.delete('page');
    return url.pathname + url.search + url.hash;
  }

  function searchBackLinks(root) {
    if (!root.matches('[data-sfs-query-filters]')) return [];
    var groups = [
      ['brand_canonical', 'brands', 'brand'], ['product_type', 'types', ''],
      ['product_class', 'categories', ''], ['series', 'series', 'series'],
      ['ampere_rating_amps', 'amps', 'amps'], ['pole_count', 'poles', 'poles'],
      ['rated_voltage_options', 'voltages', 'voltage'], ['aic_min_options', 'ratings', ''],
      ['mounting_type', 'mountings', 'mounting'], ['neutral_connection', 'neutrals', ''],
      ['conditions_available', 'conditions', '']
    ];
    return groups.map(function (group) {
      return { label: 'All ' + group[1], href: withoutFacet(window.location.href, group[0]), kind: group[2] };
    }).filter(function (link) { return !!link.href; });
  }

  function render() {
    queued = false;
    var root = document.querySelector(ROOT);
    if (!root) return;
    if (root !== currentRoot) {
      if (observer) observer.disconnect();
      currentRoot = root;
      signature = '';
      skipped.clear();
      hadFilters = false;
      searchGroups = [];
      observer = new MutationObserver(function (records) {
        if (records.some(function (record) {
          var target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
          return target && !target.closest('[data-facet-guide]');
        })) schedule();
      });
      observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['aria-current', 'aria-busy', 'aria-disabled', 'hidden', 'data-sfs-unavailable', 'data-sfs-guide-redundant', 'href'] });
    }
    var anchor = root.querySelector('.collection-results-body, .collection-results-wrapper .list-products');
    if (!anchor) return;
    var guide = root.querySelector('[data-facet-guide]');
    if (!guide) {
      guide = document.createElement('section');
      guide.className = 'facet-guide';
      guide.setAttribute('data-facet-guide', '');
      guide.setAttribute('data-sb-surface', 'guided_filters');
      guide.setAttribute('aria-label', 'Narrow your results');
      anchor.before(guide);
      if (sizeObserver) sizeObserver.disconnect();
      sizeObserver = new ResizeObserver(function (entries) {
        var width = entries[0].contentRect.width;
        if (width !== guideWidth) {
          guideWidth = width;
          fitOptions();
        }
      });
      sizeObserver.observe(guide);
    }
    if (busy(root)) {
      guide.setAttribute('aria-busy', 'true');
      guide.querySelectorAll('button, input').forEach(function (button) { button.disabled = true; });
      return;
    }
    guide.removeAttribute('aria-busy');
    var backLinks = searchBackLinks(root);
    searchGroups.forEach(function (kind) {
      if (!backLinks.some(function (link) { return link.kind === kind; })) {
        skipped.delete(kind);
        restoreGuideFocus = true;
      }
    });
    searchGroups = backLinks.map(function (link) { return link.kind; }).filter(Boolean);
    var next = nextGroup(root);
    if (!next && backLinks.length) next = { label: 'Filters', links: [], group: null, kind: '' };
    var visibleLinks = next ? next.links : [];
    var extraLinks = next && next.kind === 'poles' ? availableSpecialtyLinks(root, next.group) : [];
    var tailRemainingCount = next ? tailRemaining(root, next.group) : 0;
    var key = next ? JSON.stringify([next.label, visibleLinks.map(function (link) {
      return [labelOf(link), link.getAttribute('href')];
    }), extraLinks.map(function (link) {
      return [labelOf(link), link.getAttribute('href')];
    }), tailRemainingCount, backLinks]) : '';
    guide.hidden = !next;
    if (!next) {
      signature = '';
      if (guide.contains(document.activeElement) && document.activeElement.blur) {
        document.activeElement.blur();
      }
      restoreGuideFocus = false;
      return;
    }
    guide.querySelectorAll('button, input').forEach(function (button) { button.disabled = false; });
    if (signature === key && renderedGroup === next.group && guide.childElementCount) return;
    var active = document.activeElement;
    var hadFocus = guide.contains(active) || restoreGuideFocus;
    restoreGuideFocus = false;
    signature = key;
    if (renderedGroup !== next.group) mobileOpen = false;
    renderedGroup = next.group;
    guide.replaceChildren();
    var heading = document.createElement('h2');
    heading.className = 'facet-guide__heading';
    var guideLabel = extraLinks.length ? 'Poles or Protection' : next.label;
    heading.textContent = next.group ? 'Choose ' + guideLabel : guideLabel;
    heading.tabIndex = -1;
    guide.appendChild(heading);
    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'facet-guide__toggle';
    toggle.textContent = heading.textContent;
    toggle.setAttribute('aria-expanded', String(mobileOpen));
    toggle.setAttribute('aria-controls', 'facet-guide-content');
    guide.classList.toggle('facet-guide--open', mobileOpen);
    toggle.addEventListener('click', function () {
      mobileOpen = !mobileOpen;
      guide.classList.toggle('facet-guide--open', mobileOpen);
      toggle.setAttribute('aria-expanded', String(mobileOpen));
      fitOptions();
    });
    guide.appendChild(toggle);
    var content = document.createElement('div');
    content.className = 'facet-guide__content';
    content.id = 'facet-guide-content';
    guide.appendChild(content);
    var options = document.createElement('div');
    options.className = 'facet-guide__options';
    options.id = 'facet-guide-options';
    var optionCount = visibleLinks.length + extraLinks.length + backLinks.length;
    var expanded = expandedGroup === next.group;
    backLinks.forEach(function (back) {
      var link = document.createElement('a');
      link.className = 'facet-guide__option';
      link.textContent = back.label;
      link.href = back.href;
      link.setAttribute('data-sb-analytics-surface', 'guided_filters');
      options.appendChild(link);
    });
    visibleLinks.forEach(function (link) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'facet-guide__option';
      button.textContent = labelOf(link);
      button.addEventListener('click', function () {
        if (busy(root) || !link.isConnected || !available(link)) return;
        restoreGuideFocus = true;
        link.setAttribute('data-sb-analytics-surface', 'guided_filters');
        try { link.click(); } finally { link.removeAttribute('data-sb-analytics-surface'); }
        schedule();
      });
      options.appendChild(button);
    });
    if (extraLinks.length) {
      extraLinks.forEach(function (source) {
        var link = document.createElement('a');
        var extraLabel = labelOf(source);
        link.className = 'facet-guide__option facet-guide__specialty';
        link.textContent = extraLabel;
        link.setAttribute('aria-label', 'Protection & specialty: ' + extraLabel);
        link.href = source.getAttribute('href');
        link.addEventListener('click', function (event) {
          if (busy(root) || !source.isConnected || !available(source) || selected(source)) {
            event.preventDefault();
            return;
          }
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          restoreGuideFocus = true;
          source.setAttribute('data-sb-analytics-surface', 'guided_filters');
          try { source.click(); } finally { source.removeAttribute('data-sb-analytics-surface'); }
          schedule();
        });
        options.appendChild(link);
      });
    }
    content.appendChild(options);
    if (next.group && (optionCount > 1 || tailRemainingCount > 0)) {
      var more = document.createElement('button');
      more.type = 'button';
      more.className = 'facet-guide__more';
      more.textContent = expanded ? 'Show less' : 'Show more';
      more.setAttribute('aria-label', (expanded ? 'Show fewer ' : 'Show more ') + guideLabel + ' options');
      more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      more.setAttribute('aria-controls', 'facet-guide-options');
      more.addEventListener('click', function () {
        // Reveal every value still hidden in the left rail's own
        // data-sfs-more-options tail BEFORE re-rendering, so the buttons
        // this render pass builds are drawn from the complete list, not
        // just the first 12. Driving the left rail's real control (rather
        // than duplicating expandMoreOptions here) keeps both rails in
        // sync and keeps the reversibility rule (hidden, never removed)
        // that control already honours.
        if (!expanded) {
          var tailButton = tailButtonFor(root, next.group);
          if (tailButton) tailButton.click();
        }
        expandedGroup = expanded ? null : next.group;
        signature = '';
        render();
        var control = guide.querySelector('.facet-guide__more');
        if (control) control.focus({ preventScroll: true });
      });
      options.appendChild(more);
    }
    if (followingGroup(root, next)) {
      var skip = document.createElement('button');
      skip.type = 'button';
      skip.className = 'facet-guide__skip';
      skip.textContent = 'Skip';
      skip.addEventListener('click', function () {
        if (busy(root)) return;
        skipped.add(skipKey(next.kind, next.label));
        signature = '';
        restoreGuideFocus = true;
        render();
      });
      content.appendChild(skip);
    }
    fitOptions = function () {
      if (!options.isConnected || !options.clientWidth) return;
      var buttons = Array.prototype.slice.call(options.querySelectorAll('.facet-guide__option'));
      buttons.forEach(function (button) { button.hidden = false; button.style.maxWidth = ''; });
      if (!more) return;
      more.hidden = true;
      var rows = window.matchMedia('(max-width: 749px)').matches ? 2 : 1;
      function rowCount() {
        return new Set(Array.prototype.filter.call(options.children, function (item) {
          return !item.hidden;
        }).map(function (item) { return item.offsetTop; })).size;
      }
      // The row-overflow count alone is not the whole story: a group can
      // fit its first 12 values on one row and still have values 13+
      // waiting in the left rail's data-sfs-more-options tail (see
      // tailRemaining above). Either source of hidden values, or both
      // together, must add up to one truthful total on the button.
      if (rowCount() <= rows && !tailRemainingCount) return;
      more.hidden = false;
      if (expanded) { more.textContent = 'Show less'; return; }
      var shown = buttons.length;
      if (rowCount() > rows) {
        do {
          shown -= 1;
          buttons[shown].hidden = true;
          more.textContent = 'Show ' + (buttons.length - shown + tailRemainingCount) + ' more';
        } while (shown > 1 && rowCount() > rows);
        if (rows === 1 && rowCount() > rows) {
          buttons[0].style.maxWidth = Math.max(44, options.clientWidth - more.offsetWidth - 6) + 'px';
        }
      } else {
        more.textContent = 'Show ' + tailRemainingCount + ' more';
      }
    };
    fitOptions();
    if (hadFocus) {
      (window.matchMedia('(max-width: 749px)').matches ? toggle : heading).focus({ preventScroll: true });
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(render);
  }

  window.addEventListener('resize', function () { window.requestAnimationFrame(function () { fitOptions(); }); });
  if (document.fonts) document.fonts.ready.then(function () { fitOptions(); });
  window.addEventListener('sfs:results-updated', schedule);
  window.addEventListener('popstate', schedule);
  document.addEventListener('change', schedule);
  document.addEventListener('input', function (event) {
    if (event.target.matches('[data-sfs-option-search], [data-sfs-query-option-search]')) schedule();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
})();
